import React, { useState, useEffect, useCallback } from 'react';
import { ModalContainer, ModalContent, Form, Label, Button, Input, Select, Option, OptionGroup } from './StyledComponents';
import AddAccountModal from './AddAccountModal';
import { addAccount } from '../firebase/firebaseUtils';

const AddTransactionModal = ({ isOpen, onClose, onSave, accounts, birthDate, userId, initialTransaction }) => {
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [fromAccount, setFromAccount] = useState('');
  const [toAccount, setToAccount] = useState('');
  const [date, setDate] = useState('');
  const [recurrence, setRecurrence] = useState('');
  const [recurrenceEndDate, setRecurrenceEndDate] = useState('');
  const [recurrenceInterval, setRecurrenceInterval] = useState(1);
  const [isAddAccountModalOpen, setIsAddAccountModalOpen] = useState(false);
  const [activeAccountField, setActiveAccountField] = useState(null); // 'from' or 'to'

  const sanitizeDateInput = useCallback((value, fallback) => {
    if (!value && value !== 0) return fallback;
    if (value instanceof Date) {
      return value.toISOString().split('T')[0];
    }
    if (typeof value === 'string') {
      const match = value.match(/\d{4}-\d{2}-\d{2}/);
      if (match) return match[0];
    }
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toISOString().split('T')[0];
    }
    return fallback;
  }, []);

  useEffect(() => {
    if (isOpen) {
      const today = new Date().toISOString().split('T')[0];
      const defaultRecurrenceEnd = (() => {
        if (!birthDate) return '';
        const def = new Date(birthDate);
        def.setFullYear(def.getFullYear() + 100);
        return def.toISOString().split('T')[0];
      })();

      const transactionDescription = initialTransaction?.description || initialTransaction?.rawDescription || '';
      setDescription(transactionDescription);

      if (initialTransaction && initialTransaction.amount !== undefined && initialTransaction.amount !== null) {
        setAmount(String(initialTransaction.amount));
      } else {
        setAmount('');
      }

      setFromAccount(initialTransaction?.fromAccount || '');
      setToAccount(initialTransaction?.toAccount || '');
      setDate(sanitizeDateInput(initialTransaction?.date, today));

      const initialRecurrence = initialTransaction?.recurrence || '';
      setRecurrence(initialRecurrence);

      if (initialRecurrence) {
        setRecurrenceEndDate(sanitizeDateInput(initialTransaction?.recurrenceEndDate, defaultRecurrenceEnd || today));
      } else if (birthDate) {
        setRecurrenceEndDate(defaultRecurrenceEnd);
      } else {
        setRecurrenceEndDate('');
      }

      const intervalSource = initialTransaction?.recurrenceInterval;
      const parsedInterval = parseInt(intervalSource, 10);
      setRecurrenceInterval(Number.isFinite(parsedInterval) && parsedInterval > 0 ? parsedInterval : 1);
    }
  }, [isOpen, birthDate, initialTransaction, sanitizeDateInput]);

  const groupedAccounts = React.useMemo(() => {
    const groups = {};
    Object.entries(accounts || {}).forEach(([id, account]) => {
      if (!account || account.archived) return;
      const type = account.type || 'Other';
      if (!groups[type]) groups[type] = [];
      groups[type].push({ id, name: account.name });
    });
    Object.keys(groups).forEach(type => {
      groups[type].sort((a, b) => a.name.localeCompare(b.name));
    });
    return Object.keys(groups)
      .sort()
      .reduce((acc, type) => {
        acc[type] = groups[type];
        return acc;
      }, {});
  }, [accounts]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const newTransaction = {
      description,
      amount: parseFloat(amount),
      fromAccount,
      toAccount,
      date,
      recurrence,
      recurrenceEndDate,
      recurrenceInterval: parseInt(recurrenceInterval, 10),
      isPaid: false,
    };
    console.log("Submitting transaction: ", newTransaction);
    onSave(newTransaction);
    onClose();
  };

  const handleRecurrenceChange = (e) => {
    setRecurrence(e.target.value);
  };

  const handleFromAccountChange = (e) => {
    const value = e.target.value;
    if (value === 'add_new') {
      setActiveAccountField('from');
      setIsAddAccountModalOpen(true);
    } else {
      setFromAccount(value);
    }
  };

  const handleToAccountChange = (e) => {
    const value = e.target.value;
    if (value === 'add_new') {
      setActiveAccountField('to');
      setIsAddAccountModalOpen(true);
    } else {
      setToAccount(value);
    }
  };

  if (!isOpen) return null;

  return (
    <>
    <ModalContainer>
      <ModalContent>
        <Form onSubmit={handleSubmit}>
          <Label>Description:</Label>
          <Input value={description} onChange={(e) => setDescription(e.target.value)} required />
          <Label>Amount:</Label>
          <Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} required />
          <Label>Date:</Label>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
          <Label>From Account:</Label>
          <Select value={fromAccount} onChange={handleFromAccountChange} required>
            <option value="">Select Account</option>
            {Object.keys(groupedAccounts).map(type => (
              <OptionGroup key={type} label={type}>
                {groupedAccounts[type].map(acc => (
                  <Option key={acc.id} value={acc.id}>{acc.name}</Option>
                ))}
              </OptionGroup>
            ))}
            <Option value="add_new">+ Add Account</Option>
          </Select>
          <Label>To Account:</Label>
          <Select value={toAccount} onChange={handleToAccountChange} required>
            <option value="">Select Account</option>
            {Object.keys(groupedAccounts).map(type => (
              <OptionGroup key={type} label={type}>
                {groupedAccounts[type].map(acc => (
                  <Option key={acc.id} value={acc.id}>{acc.name}</Option>
                ))}
              </OptionGroup>
            ))}
            <Option value="add_new">+ Add Account</Option>
          </Select>
          <Label>Recurrence:</Label>
          <Select value={recurrence} onChange={handleRecurrenceChange}>
            <Option value="">None</Option>
            <Option value="weekly">Weekly</Option>
            <Option value="monthly">Monthly</Option>
            <Option value="annually">Annually</Option>
            <Option value="custom">Custom</Option>
          </Select>
          {recurrence && recurrence !== 'custom' && (
            <>
              <Label>
                Every {recurrence === 'weekly' ? 'X Weeks' : recurrence === 'monthly' ? 'X Months' : 'X Years'}:
              </Label>
              <Input
                type="number"
                min="1"
                value={recurrenceInterval}
                onChange={(e) => setRecurrenceInterval(e.target.value)}
                required
              />
              <Label>Recurrence End Date:</Label>
              <Input
                type="date"
                value={recurrenceEndDate}
                onChange={(e) => setRecurrenceEndDate(e.target.value)}
                required
              />
            </>
          )}
          {recurrence === 'custom' && (
            <>
              <Label>Recurrence Interval (days):</Label>
              <Input
                type="number"
                min="1"
                value={recurrenceInterval}
                onChange={(e) => setRecurrenceInterval(e.target.value)}
                required
              />
              <Label>Recurrence End Date:</Label>
              <Input
                type="date"
                value={recurrenceEndDate}
                onChange={(e) => setRecurrenceEndDate(e.target.value)}
                required
              />
            </>
          )}
          <Button type="submit">Add Transaction</Button>
          <Button
            type="button"
            onClick={() => {
              console.log("Closing modal");
              onClose();
            }}
          >
            Close
          </Button>
        </Form>
      </ModalContent>
    </ModalContainer>
    <AddAccountModal
      isOpen={isAddAccountModalOpen}
      onClose={() => setIsAddAccountModalOpen(false)}
      onSave={async (account) => {
        const newId = await addAccount(userId, account);
        if (activeAccountField === 'from') {
          setFromAccount(newId);
        } else if (activeAccountField === 'to') {
          setToAccount(newId);
        }
        setIsAddAccountModalOpen(false);
      }}
    />
    </>
  );
};

export default AddTransactionModal;
